import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Loader2, Lock, Heart, Check, AlertCircle, Sparkles, BookOpen, Volume2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import supabase from '../supabaseClient';
import InteractiveJournalSection from '../components/vault/InteractiveJournalSection';
import { AffirmationCardsEdit, AffirmationsHeading } from '../components/vault/AffirmationCards';
import { PocketKeepsakeCardEdit } from '../components/vault/PocketKeepsakeCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const AUDIO_BUCKET = 'memory-audio';

/* sessionStorage key for the once-per-session edit unlock. Scoped per-QR so
   unlocking one vault never grants access to another. Cleared by the browser
   the moment the tab/window is closed — never written to localStorage. */
const editUnlockKey = (qr) => `s2m-vault-edit-unlocked:${qr}`;
const isEditUnlocked = (qr) => {
  try { return window.sessionStorage.getItem(editUnlockKey(qr)) === '1'; }
  catch { return false; }
};
const markEditUnlocked = (qr) => {
  try { window.sessionStorage.setItem(editUnlockKey(qr), '1'); } catch {}
};

/* Sibling-selection acknowledgement: once the buyer has chosen which vault to
   open from a multi-vault order, we don't show the selection screen again on
   this QR for the rest of the session. Per-QR scoped — so the FIRST landing
   on a different sibling QR (e.g. clicking "Begin Vault 3" from email after
   editing Vault 2) STILL shows the selection screen, preserving the "see all
   your vaults" intent for every fresh email link click. */
const siblingsAckKey = (qr) => `s2m-vault-siblings-ack:${qr}`;
const isSiblingsAck = (qr) => {
  try { return window.sessionStorage.getItem(siblingsAckKey(qr)) === '1'; }
  catch { return false; }
};
const markSiblingsAck = (qr) => {
  try { window.sessionStorage.setItem(siblingsAckKey(qr), '1'); } catch {}
};

/* ── Sent2ment palette (cream / midnight blue / champagne gold) ── */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  midnight: '#0B1F3A',
  gold: '#C6A96B',
  goldSoft: 'rgba(198, 169, 107, 0.18)',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
  border: 'rgba(198, 169, 107, 0.25)',
};

const fadeUp = {
  initial: { y: 16, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

/* ── Vault content schema (mirrors backend) ── */
const TEXT_FIELDS = {
  pocket_keepsake_message: {
    label: 'Your written message',
    placeholder: 'Something gentle for them to carry close…',
    rows: 4,
  },
  affirmation_message_1: {
    label: 'Affirmation 1 — written',
    placeholder: 'A quiet reminder for a heavy day…',
    rows: 3,
  },
  affirmation_message_2: {
    label: 'Affirmation 2 — written',
    placeholder: 'A second note of strength…',
    rows: 3,
  },
  affirmation_message_3: {
    label: 'Affirmation 3 — written',
    placeholder: 'One more, just for them…',
    rows: 3,
  },
  journal_page_1_message: {
    label: 'The Beginning · Page 1',
    placeholder: 'For when they first open the journal…',
    rows: 5,
  },
  journal_page_7_message: {
    label: 'A gentle moment · Page 7',
    placeholder: 'A soft note for a week in…',
    rows: 5,
  },
  journal_page_18_message: {
    label: 'A quiet reminder · Page 18',
    placeholder: 'For when they need to be reminded who they are…',
    rows: 5,
  },
  journal_page_30_message: {
    label: 'When you need it most · Page 30',
    placeholder: 'Something deeper, for the long road ahead…',
    rows: 6,
  },
};

const AUDIO_SLOTS = [
  { slot: 'extra',         column: 'vault_extra_audio_url',        label: 'Voice message', helper: '' },
  { slot: 'pocket',        column: 'pocket_keepsake_audio_url',    label: 'Spoken version', helper: 'Up to 1 minute. Optional — record the same words in your own voice.' },
  { slot: 'affirmation_1', column: 'affirmation_1_audio_url',      label: 'Affirmation 1 — spoken', helper: 'Up to 15 seconds.' },
  { slot: 'affirmation_2', column: 'affirmation_2_audio_url',      label: 'Affirmation 2 — spoken', helper: 'Up to 15 seconds.' },
  { slot: 'affirmation_3', column: 'affirmation_3_audio_url',      label: 'Affirmation 3 — spoken', helper: 'Up to 15 seconds.' },
];

/* Per-field limits — kept tight so the messages feel intentional. */
const WORD_LIMITS = {
  pocket_keepsake_message: 30,
  affirmation_message_1: 30,
  affirmation_message_2: 30,
  affirmation_message_3: 30,
  // journal_page_*_message — no word limit (long-form is allowed)
};

/* Per-slot audio duration limits in seconds. Falsy = no limit. */
const AUDIO_DURATION_LIMITS = {
  pocket: 60,            // 1 minute
  affirmation_1: 15,
  affirmation_2: 15,
  affirmation_3: 15,
  // extra (main vault voice message) — currently no limit
};

const countWords = (s) => (String(s || '').trim().match(/\S+/g) || []).length;

/* ── Small UI atoms ── */
const SectionHeading = ({ overline, title, body, Icon }) => (
  <div className="mb-8 sm:mb-10 text-center sm:text-left">
    <div className="flex items-center justify-center sm:justify-start gap-3 mb-3">
      {Icon && <Icon size={18} strokeWidth={1.4} style={{ color: C.gold }} />}
      <p className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: C.gold }}>{overline}</p>
    </div>
    <h2 className="text-2xl sm:text-3xl font-light leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
      {title}
    </h2>
    {body && <p className="text-sm leading-relaxed max-w-2xl" style={{ color: C.textSec, fontFamily: "'Cormorant Garamond', serif" }}>{body}</p>}
  </div>
);

const TextField = ({ name, value, onChange, locked }) => {
  const cfg = TEXT_FIELDS[name];
  const wordLimit = WORD_LIMITS[name];
  const wordsUsed = countWords(value);
  const wordsLeft = wordLimit ? Math.max(0, wordLimit - wordsUsed) : null;
  const overLimit = wordLimit && wordsUsed > wordLimit;

  // Soft-trim when the user types past the limit so the limit is enforceable
  // without ripping the cursor mid-type.
  const handleChange = (e) => {
    const next = e.target.value;
    if (!wordLimit) { onChange(name, next); return; }
    const tokens = next.trim().match(/\S+/g) || [];
    if (tokens.length <= wordLimit) {
      onChange(name, next);
    } else {
      // Keep first N words exactly and preserve the trailing whitespace style
      const trimmed = tokens.slice(0, wordLimit).join(' ');
      // Preserve final newlines/spaces if user was mid-typing punctuation
      onChange(name, trimmed);
    }
  };

  return (
    <div className="mb-5" data-testid={`vault-edit-text-${name}`}>
      <label className="block text-[11px] tracking-[0.18em] uppercase font-medium mb-2" style={{ color: C.midnight }}>
        {cfg.label}
      </label>
      <textarea
        value={value || ''}
        onChange={handleChange}
        placeholder={cfg.placeholder}
        rows={cfg.rows}
        disabled={locked}
        maxLength={5000}
        data-testid={`vault-edit-textarea-${name}`}
        className="w-full px-4 py-3 text-[15px] outline-none transition-colors duration-300 resize-none disabled:opacity-60"
        style={{
          background: locked ? C.paper : '#FFFFFF',
          color: C.text,
          border: `1px solid ${overLimit ? '#B33A3A' : C.border}`,
          fontFamily: 'Georgia, serif',
          lineHeight: '1.7',
        }}
      />
      {wordLimit && (
        <p
          className="text-[11px] italic mt-2 text-right"
          style={{ color: overLimit ? '#B33A3A' : `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
          data-testid={`vault-edit-wordcount-${name}`}
        >
          {wordsLeft} word{wordsLeft === 1 ? '' : 's'} left · {wordLimit} max
        </p>
      )}
    </div>
  );
};

const AudioSlot = ({ slot, label, helper, currentUrl, onUpload, locked, busy }) => {
  const inputRef = useRef(null);
  const handlePick = () => { if (!locked && !busy) inputRef.current?.click(); };
  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) onUpload(slot, file);
  };
  return (
    <div className="mb-5" data-testid={`vault-edit-audio-${slot}`}>
      <label className="block text-[11px] tracking-[0.18em] uppercase font-medium mb-2" style={{ color: C.midnight }}>
        {label}
      </label>
      {helper && <p className="text-xs italic mb-2" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>{helper}</p>}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={handlePick}
          disabled={locked || busy}
          data-testid={`vault-edit-audio-pick-${slot}`}
          className="inline-flex items-center gap-2 px-5 py-3 text-xs tracking-[0.18em] uppercase font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ border: `1px solid ${C.gold}`, color: C.midnight, background: currentUrl ? C.goldSoft : 'transparent' }}
        >
          {busy ? (
            <><Loader2 size={14} className="animate-spin" /> Uploading…</>
          ) : currentUrl ? (
            <><Check size={14} /> Replace recording</>
          ) : (
            <><Mic size={14} /> Add audio</>
          )}
        </button>
        {currentUrl && (
          <audio controls preload="metadata" src={currentUrl} className="h-9 max-w-xs" data-testid={`vault-edit-audio-player-${slot}`} />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.oga,.opus,.webm,.amr,.3gp,.3gpp,.flac"
        onChange={handleChange}
        className="hidden"
        data-testid={`vault-edit-audio-input-${slot}`}
      />
    </div>
  );
};

/* ── Page ── */
const VaultEditPage = () => {
  const params = useParams();
  const qr_code = params.qr_code || params.qrCode || '';

  const [phase, setPhase] = useState('loading'); // loading | select | gate | edit | locked | notfound | error
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const [content, setContent] = useState({});            // text + audio URLs
  const [textDraft, setTextDraft] = useState({});        // local edit buffer
  const [audioBusy, setAudioBusy] = useState({});        // per-slot busy flag
  const [saveStatus, setSaveStatus] = useState('idle');  // idle | saving | saved | error
  const [finaliseOpen, setFinaliseOpen] = useState(false);
  const [finalising, setFinalising] = useState(false);

  // Multi-vault siblings (ordered list of QRs from the same purchase). For a
  // single-vault buyer this is just [qr_code] and the selection screen is
  // skipped automatically. Fetched once on load from /api/vault/{qr}/siblings.
  const [siblings, setSiblings] = useState([]);

  // Buyer passcode-gate state (NEW). When meta.has_password is true and the
  // current browser session hasn't unlocked this QR yet, the editor blocks on
  // a passcode entry. Verified server-side via /api/vault/{qr}/unlock — same
  // endpoint the recipient viewer uses, so we never duplicate logic or trust
  // the client.
  const [gatePwd, setGatePwd] = useState('');
  const [gateShowPwd, setGateShowPwd] = useState(false);
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateWrongCount, setGateWrongCount] = useState(0);

  // Passcode editor state — reuses the existing /api/memory/{qr}/set-password endpoint
  // (the vault deliberately uses the same password_hash as the QR memory — no separate vault code).
  const [pwd, setPwd] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdHint, setPwdHint] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMessage, setPwdMessage] = useState({ kind: '', text: '' });
  // Current passcode — required by backend when replacing an existing passcode
  // (Prompt 33 security fix). Auto-populated from the gate value below so the
  // user isn't asked to type it twice in the same session.
  const [pwdCurrent, setPwdCurrent] = useState('');

  // Optional recipient email (sent only after Finalise & Send)
  const [sendMethod, setSendMethod] = useState('self'); // 'self' | 'direct'
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientMessage, setRecipientMessage] = useState('');

  /* ── Load just the editor content. Called either after the gate passes,
       or directly when there's no passcode set on this vault.
       Task 10 L2 fix: when a passcode is set, send it via the
       ``X-Memory-Passcode`` header so the backend returns signed vault
       audio URLs. The passcode is already cached in ``pwdCurrent`` after
       a successful ``/unlock`` call — we never ask the buyer twice. ── */
  const loadEditContent = useCallback(async (passcodeOverride) => {
    try {
      const effectivePwd = passcodeOverride || pwdCurrent || '';
      const headers = {};
      if (effectivePwd) headers['X-Memory-Passcode'] = effectivePwd;
      const contentRes = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/edit-content`, { cache: 'no-store', headers });
      if (!contentRes.ok) {
        setError('We couldn’t open the Memory Vault right now. Please try again soon.');
        setPhase('error');
        return;
      }
      const data = await contentRes.json();
      setContent(data);
      const initialText = {};
      Object.keys(TEXT_FIELDS).forEach((k) => { initialText[k] = data[k] || ''; });
      setTextDraft(initialText);
      setSendMethod(data.recipient_send_method === 'direct' ? 'direct' : 'self');
      setRecipientEmail(data.recipient_email_address || '');
      setRecipientMessage(data.recipient_personal_message || '');
      setPhase(data.vault_locked ? 'locked' : 'edit');
    } catch (e) {
      setError('Connection failed. Please check your internet and try again.');
      setPhase('error');
    }
  }, [qr_code, pwdCurrent]);

  /* ── Initial load ── */
  const loadVault = useCallback(async () => {
    // Hard-reset all per-vault state at the start so a previously-loaded vault's
    // meta (passcode, hint, content, recipient) can NEVER bleed into a new
    // /vault-edit/<QR> while the new fetch is in flight.
    setPhase('loading');
    setError('');
    setMeta(null);
    setContent({});
    setTextDraft({});
    setPwd('');
    setPwdConfirm('');
    setPwdHint('');
    setPwdCurrent('');
    setPwdMessage({ kind: '', text: '' });
    setSendMethod('self');
    setRecipientEmail('');
    setRecipientMessage('');
    setGatePwd('');
    setGateShowPwd(false);
    setGateSubmitting(false);
    setGateWrongCount(0);
    setSiblings([]);

    try {
      // cache: 'no-store' defeats iOS in-app browser (Outlook/Gmail) HTTP caching
      // so a previously-viewed vault's meta cannot be served for this QR.
      const metaRes = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/meta`, { cache: 'no-store' });
      if (metaRes.status === 404) { setPhase('notfound'); return; }
      if (!metaRes.ok) {
        setError('We couldn’t reach the Memory Vault right now. Please try again soon.');
        setPhase('error');
        return;
      }
      const metaData = await metaRes.json();
      setMeta(metaData);

      if (!metaData.vault_enabled) {
        setError('No Memory Vault is linked to this code yet.');
        setPhase('notfound');
        return;
      }

      // Multi-vault: fetch sibling QRs in parallel-ish (best effort, never blocks
      // the vault flow). If this QR is part of a 2+ vault purchase AND the buyer
      // hasn't yet acknowledged the selection screen on this QR in this session,
      // show them the calm "Your Memory Vaults" picker first.
      let sibs = [qr_code];
      try {
        const sibRes = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/siblings`, { cache: 'no-store' });
        if (sibRes.ok) {
          const sibData = await sibRes.json();
          if (Array.isArray(sibData?.qrs) && sibData.qrs.length > 0) {
            sibs = sibData.qrs;
          }
        }
      } catch { /* siblings is best-effort; never block the vault flow */ }
      setSiblings(sibs);

      if (sibs.length > 1 && !isSiblingsAck(qr_code)) {
        setPhase('select');
        return;
      }

      // Hard guard: if a passcode is set on this vault AND the current browser
      // session hasn't unlocked it, force the gate first — even for the buyer.
      // This protects against forwarded buyer-confirmation emails being opened
      // on someone else's device, and against Outlook Safe Links / preloads.
      // sessionStorage clears the moment the tab/window closes, so the next
      // session starts locked again.
      if (metaData.has_password && !isEditUnlocked(qr_code)) {
        setPhase('gate');
        return;
      }

      // Otherwise (no passcode set, or already unlocked this session) load the
      // editor content directly. This endpoint does not require a passcode —
      // server-side enforcement happens at /unlock above.
      await loadEditContent();
    } catch (e) {
      setError('Connection failed. Please check your internet and try again.');
      setPhase('error');
    }
  }, [qr_code, loadEditContent]);

  useEffect(() => { loadVault(); }, [loadVault]);

  /* ── Buyer-side passcode unlock — verifies via /api/vault/{qr}/unlock ── */
  const handleGateSubmit = async (e) => {
    e.preventDefault();
    if (!gatePwd) return;
    setGateSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: gatePwd }),
        cache: 'no-store',
      });
      if (res.status === 401) {
        setGateWrongCount((c) => c + 1);
        setGateSubmitting(false);
        return;
      }
      if (!res.ok) {
        setError('Something went wrong. Please try again.');
        setPhase('error');
        setGateSubmitting(false);
        return;
      }
      // Successful verification — remember for the rest of this browser session.
      markEditUnlocked(qr_code);
      // Cache the correct current passcode so the passcode replacement form
      // can send it without asking the buyer to type it twice. Cleared after
      // a successful passcode save.
      setPwdCurrent(gatePwd);
      const unlockedPwd = gatePwd;
      setGatePwd('');
      setGateSubmitting(false);
      // Now load editor content; this leaves phase='edit' (or 'locked' if sealed).
      await loadEditContent(unlockedPwd);
    } catch (err) {
      setError('Connection failed. Please try again.');
      setPhase('error');
      setGateSubmitting(false);
    }
  };

  /* ── Text editing + debounced save ── */
  const saveTimerRef = useRef(null);
  const handleTextChange = (name, value) => {
    setTextDraft((prev) => ({ ...prev, [name]: value }));
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persistText({ ...textDraft, [name]: value }), 900);
  };

  const persistText = async (draft) => {
    try {
      // Only send the keys that exist in our schema; backend whitelists too, but this keeps payloads tidy.
      const payload = {};
      Object.keys(TEXT_FIELDS).forEach((k) => {
        // Send empty string if user cleared the field, so backend overwrites stale text.
        // (The backend will trim and persist as-is.)
        if (draft[k] !== undefined && draft[k] !== null) payload[k] = String(draft[k]);
      });
      // Drop completely-empty payloads (no fields touched yet)
      const hasAnything = Object.values(payload).some((v) => v !== undefined);
      if (!hasAnything) { setSaveStatus('idle'); return; }

      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { setSaveStatus('error'); return; }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (e) {
      setSaveStatus('error');
    }
  };

  /* Probe audio duration before uploading. Resolves with seconds (Infinity if unknown). */
  const probeAudioDuration = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const a = new Audio();
      const cleanup = () => URL.revokeObjectURL(url);
      a.onloadedmetadata = () => { const d = a.duration; cleanup(); resolve(Number.isFinite(d) ? d : Infinity); };
      a.onerror = () => { cleanup(); resolve(Infinity); };
      a.preload = 'metadata';
      a.src = url;
    } catch { resolve(Infinity); }
  });

  /* ── Audio upload — proxied through backend (Task 13).
       The browser no longer writes to Supabase Storage directly. We POST
       the file as multipart to /api/vault/{qr}/upload-audio which uses the
       backend service-role key, enforces vault_locked server-side, maps
       extensions/MIME, and persists the path. The returned ``url`` is
       stored as-is so _mint_signed_media_url can sign it at read time. ── */
  const handleAudioUpload = async (slot, file) => {
    // Server-side enforcement of duration limits
    const limit = AUDIO_DURATION_LIMITS[slot];
    if (limit) {
      const dur = await probeAudioDuration(file);
      if (dur > limit + 0.5) {
        alert('This recording is a little too long. Please keep this message within the time limit.');
        return;
      }
    }
    setAudioBusy((prev) => ({ ...prev, [slot]: true }));
    try {
      const cfg = AUDIO_SLOTS.find((a) => a.slot === slot);
      if (!cfg) throw new Error('invalid slot');

      const form = new FormData();
      form.append('slot', slot);
      form.append('audio', file, file.name);

      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/upload-audio`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || 'We couldn\u2019t upload this audio file. Please try an MP3, M4A, WAV or voice recording.');
        return;
      }

      setContent((prev) => ({ ...prev, [cfg.column]: data.url }));
    } catch (e) {
      // Network or server failure
      alert('We couldn\u2019t upload this audio file. Please try an MP3, M4A, WAV or voice recording.');
    } finally {
      setAudioBusy((prev) => ({ ...prev, [slot]: false }));
    }
  };

  /* ── Recipient fields — debounced auto-save through the existing /save endpoint ── */
  const recipientTimerRef = useRef(null);
  const persistRecipient = useCallback(async (payload) => {
    try {
      setSaveStatus('saving');
      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // 400 here usually means an invalid email — surfaced inline at finalise.
        setSaveStatus('error');
        return data;
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (e) {
      setSaveStatus('error');
    }
  }, [qr_code]);

  useEffect(() => {
    // Skip first render before the editor mounts
    if (phase !== 'edit') return;
    if (recipientTimerRef.current) clearTimeout(recipientTimerRef.current);
    recipientTimerRef.current = setTimeout(() => {
      // Only persist when something is meaningful to save.
      const payload = { recipient_send_method: sendMethod };
      if (sendMethod === 'direct') {
        // Allow saving even if email is partially typed; backend validates format only when non-empty.
        // We avoid POSTing invalid emails by skipping save until format looks right.
        const looksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim());
        if (looksValid) payload.recipient_email_address = recipientEmail.trim();
        if (recipientMessage !== undefined) payload.recipient_personal_message = recipientMessage;
      }
      persistRecipient(payload);
    }, 700);
    return () => recipientTimerRef.current && clearTimeout(recipientTimerRef.current);
  }, [sendMethod, recipientEmail, recipientMessage, phase, persistRecipient]);

  /* ── Passcode (optional) — uses existing /api/memory/{qr}/set-password ── */
  const handlePasscodeSave = async () => {
    setPwdMessage({ kind: '', text: '' });
    if (!pwd) {
      setPwdMessage({ kind: 'error', text: 'Please enter a passcode.' });
      return;
    }
    if (pwd !== pwdConfirm) {
      setPwdMessage({ kind: 'error', text: 'The passcodes don\u2019t match.' });
      return;
    }
    if (pwd.length < 4) {
      setPwdMessage({ kind: 'error', text: 'Please choose at least 4 characters.' });
      return;
    }
    // When replacing an existing passcode the backend now requires the current
    // one (Prompt 33 security fix). If the session came in through the gate we
    // already cached it in pwdCurrent; otherwise the buyer must type it here.
    if (meta?.has_password && !pwdCurrent) {
      setPwdMessage({ kind: 'error', text: 'Please enter your current passcode to change it.' });
      return;
    }
    setPwdSaving(true);
    try {
      const body = { password: pwd, hint: pwdHint || null };
      if (meta?.has_password) body.current_password = pwdCurrent;
      const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qr_code)}/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setPwdMessage({ kind: 'error', text: data.detail || 'Current passcode is incorrect.' });
        return;
      }
      if (!res.ok) {
        setPwdMessage({ kind: 'error', text: data.detail || 'Could not save passcode. Please try again.' });
        return;
      }
      setMeta((m) => ({ ...(m || {}), has_password: true, password_hint: pwdHint || null }));
      setPwd(''); setPwdConfirm('');
      // The passcode has been rotated — cache the NEW passcode so a further
      // change in the same session still works without re-typing.
      setPwdCurrent(pwd);
      setPwdMessage({ kind: 'success', text: 'Passcode saved for this Memory Vault.' });
    } catch (e) {
      setPwdMessage({ kind: 'error', text: 'Connection failed. Please try again.' });
    } finally {
      setPwdSaving(false);
    }
  };

  /* ── Remove passcode (only shown when meta.has_password is true) ────
     Requires proof of the current passcode (bcrypt-verified server-side).
     Idempotently clears both the hash and the hint. */
  const [pwdRemoving, setPwdRemoving] = useState(false);
  const handlePasscodeRemove = async () => {
    setPwdMessage({ kind: '', text: '' });
    if (!pwdCurrent) {
      setPwdMessage({ kind: 'error', text: 'Please enter your current passcode to remove it.' });
      return;
    }
    if (!window.confirm('Remove the passcode from this Memory Vault? It will be unlocked for anyone with the link.')) {
      return;
    }
    setPwdRemoving(true);
    try {
      const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qr_code)}/remove-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: pwdCurrent }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setPwdMessage({ kind: 'error', text: data.detail || 'Current passcode is incorrect.' });
        return;
      }
      if (res.status === 429) {
        setPwdMessage({ kind: 'error', text: 'Too many attempts. Please try again in a few minutes.' });
        return;
      }
      if (!res.ok) {
        setPwdMessage({ kind: 'error', text: data.detail || 'Could not remove passcode. Please try again.' });
        return;
      }
      setMeta((m) => ({ ...(m || {}), has_password: false, password_hint: null }));
      setPwd(''); setPwdConfirm(''); setPwdCurrent(''); setPwdHint('');
      setPwdMessage({ kind: 'success', text: 'Passcode removed. This Memory Vault is now unlocked.' });
    } catch (e) {
      setPwdMessage({ kind: 'error', text: 'Connection failed. Please try again.' });
    } finally {
      setPwdRemoving(false);
    }
  };

  /* ── Finalise ── */
  const handleFinalise = async () => {
    // Pre-flight validation for the recipient email feature
    if (sendMethod === 'direct') {
      const ok = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim());
      if (!ok) {
        alert('Please add a valid recipient email, or choose \u201CI\u2019ll share this myself\u201D.');
        return;
      }
    }
    setFinalising(true);
    try {
      // Make sure latest text + recipient drafts are persisted before locking
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (recipientTimerRef.current) clearTimeout(recipientTimerRef.current);
      await persistText(textDraft);
      await persistRecipient({
        recipient_send_method: sendMethod,
        ...(sendMethod === 'direct' ? {
          recipient_email_address: recipientEmail.trim(),
          recipient_personal_message: recipientMessage,
        } : {}),
      });

      const res = await fetch(`${API_BASE}/api/vault/${encodeURIComponent(qr_code)}/finalise`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.detail || 'Could not finalise the Memory Vault. Please try again.');
        return;
      }
      setPhase('locked');
      setFinaliseOpen(false);
      // Surface a quick confirmation if the recipient email was sent
      if (data.recipient_email_sent) {
        // The locked-state page will appear next; user feedback here is optional.
      }
    } catch (e) {
      alert('Connection failed. Please try again.');
    } finally {
      setFinalising(false);
    }
  };

  /* ── Render: loading / errors / locked / notfound ── */
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.cream }} data-testid="vault-edit-loading">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 animate-spin" size={32} strokeWidth={1.5} style={{ color: C.gold }} />
          <p className="text-sm tracking-[0.15em] uppercase" style={{ color: C.textSec }}>Opening your Memory Vault</p>
        </div>
      </div>
    );
  }

  if (phase === 'notfound') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.cream }} data-testid="vault-edit-notfound">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <div className="w-12 h-12 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: C.goldSoft }}>
            <Heart size={20} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>No Memory Vault here yet</h1>
          <p className="text-sm leading-relaxed" style={{ color: C.textSec }}>{error || "We couldn't find a Memory Vault linked to this code."}</p>
        </motion.div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: C.cream }} data-testid="vault-edit-error">
        <motion.div {...fadeUp} className="max-w-md text-center">
          <AlertCircle size={28} strokeWidth={1.4} style={{ color: C.gold }} className="mx-auto mb-5" />
          <h1 className="text-2xl sm:text-3xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>We can't open this right now</h1>
          <p className="text-sm leading-relaxed" style={{ color: C.textSec }}>{error}</p>
        </motion.div>
      </div>
    );
  }

  /* ── Render: SIBLINGS selection ("Your Memory Vaults" picker). Shown when
       this QR is part of a multi-vault purchase AND the buyer hasn't yet
       acknowledged the picker on this QR in this session. Designed so every
       fresh email-link click for a multi-vault order gives the buyer a clear
       view of all their vaults before entering one. */
  if (phase === 'select') {
    const currentIdx = siblings.indexOf(qr_code);
    const continueCurrent = () => {
      markSiblingsAck(qr_code);
      // Advance into the existing flow for THIS vault (gate or content).
      if (meta?.has_password && !isEditUnlocked(qr_code)) {
        setPhase('gate');
      } else {
        setPhase('loading');
        loadEditContent();
      }
    };
    return (
      <div className="min-h-screen px-6 py-20" style={{ background: C.cream }} data-testid="vault-edit-select">
        <motion.div {...fadeUp} className="max-w-md w-full mx-auto text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-8 flex items-center justify-center" style={{ background: C.goldSoft }}>
            <Heart size={20} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: C.gold }}>
            Your Memory Vaults
          </p>
          <h1
            className="text-2xl sm:text-3xl font-light leading-[1.25] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
            data-testid="vault-edit-select-heading"
          >
            Independent spaces, each yours to fill.
          </h1>
          <p className="text-sm leading-relaxed mb-10 px-2" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            Each vault is separate &mdash; its own passcode, voice, words, and recipient.
            Begin one whenever it feels right; the others wait quietly until you return.
          </p>

          <div className="flex flex-col gap-3 mb-8 text-left" data-testid="vault-edit-select-list">
            {siblings.map((q, i) => {
              const isCurrent = q === qr_code;
              const label = i === 0 ? 'Open your first space' : 'Continue your next space';
              if (isCurrent) {
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={continueCurrent}
                    data-testid={`vault-edit-select-current-${i + 1}`}
                    className="w-full font-semibold tracking-[0.2em] uppercase text-xs py-4 transition-colors duration-300 hover:opacity-95"
                    style={{ background: C.gold, color: C.btnText, borderRadius: '4px', boxShadow: '0 4px 20px rgba(198,169,107,0.25)' }}
                  >
                    {label} <span className="font-normal opacity-80">&middot; you&rsquo;re here</span>
                  </button>
                );
              }
              return (
                <a
                  key={q}
                  href={`/vault-edit/${encodeURIComponent(q)}`}
                  data-testid={`vault-edit-select-other-${i + 1}`}
                  className="w-full font-semibold tracking-[0.2em] uppercase text-xs py-4 transition-colors duration-300 hover:opacity-90 text-center"
                  style={{ background: 'transparent', color: C.midnight, border: `1px solid ${C.gold}`, borderRadius: '4px' }}
                >
                  {label}
                </a>
              );
            })}
          </div>

          <p
            className="text-[11px] italic"
            style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}
            data-testid="vault-edit-select-note"
          >
            {currentIdx >= 0
              ? `Space ${currentIdx + 1} of ${siblings.length} \u00b7 ready to open whenever you are.`
              : `${siblings.length} spaces \u00b7 ready to open whenever you are.`}
          </p>
        </motion.div>
      </div>
    );
  }

  /* ── Render: BUYER passcode gate. Required once per browser session when a
       passcode has been set on this vault. Closes the email-forwarding loophole
       (e.g. someone opening the buyer's order-confirmation email link from a
       shared inbox or borrowed device). Verified server-side via the same
       /unlock endpoint the recipient uses. */
  if (phase === 'gate') {    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-20" style={{ background: C.cream }} data-testid="vault-edit-gate">
        <motion.div {...fadeUp} className="max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-8 flex items-center justify-center" style={{ background: C.goldSoft }}>
            <Lock size={22} strokeWidth={1.5} style={{ color: C.gold }} />
          </div>
          <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-4" style={{ color: C.gold }}>
            Sent2ment &middot; Memory Vault
          </p>
          <h1
            className="text-2xl sm:text-3xl font-light leading-[1.25] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
            data-testid="vault-edit-gate-heading"
          >
            A passcode keeps this private
          </h1>
          <p className="text-sm sm:text-base leading-relaxed mb-8 px-2" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            You added a passcode to this Memory Vault.<br />
            Please enter it once to continue editing.
          </p>

          <form onSubmit={handleGateSubmit} className="text-left space-y-4">
            <div className="relative">
              <input
                type={gateShowPwd ? 'text' : 'password'}
                value={gatePwd}
                onChange={(e) => setGatePwd(e.target.value)}
                autoFocus
                autoComplete="current-password"
                placeholder="Passcode"
                data-testid="vault-edit-gate-password-input"
                className="w-full bg-white outline-none px-4 py-3 pr-10 text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300 text-center tracking-[0.15em]"
                style={{ color: C.midnight, border: `1px solid ${C.border}` }}
              />
              <button
                type="button"
                onClick={() => setGateShowPwd((v) => !v)}
                aria-label={gateShowPwd ? 'Hide passcode' : 'Show passcode'}
                className="absolute inset-y-0 right-0 px-3 transition-colors"
                style={{ color: C.textSec }}
              >
                {gateShowPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              </button>
            </div>

            {gateWrongCount > 0 && (
              <p className="text-xs text-red-500 text-center" data-testid="vault-edit-gate-wrong">
                That isn&rsquo;t quite right. Please try again.
              </p>
            )}

            {meta?.password_hint && (
              <p
                className="text-xs italic text-center leading-relaxed"
                style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
                data-testid="vault-edit-gate-hint"
              >
                A gentle reminder: {meta.password_hint}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={!gatePwd || gateSubmitting}
              whileHover={(!gatePwd || gateSubmitting) ? {} : { scale: 1.01 }}
              whileTap={(!gatePwd || gateSubmitting) ? {} : { scale: 0.99 }}
              data-testid="vault-edit-gate-submit-btn"
              className="w-full text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              style={{ background: C.gold }}
            >
              {gateSubmitting ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={14} /> Opening&hellip;</span>
              ) : (
                'Continue'
              )}
            </motion.button>

            <p
              className="text-[11px] italic text-center mt-4 leading-relaxed"
              style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}
            >
              You&rsquo;ll only need to do this once per browser session.
            </p>
            <p className="text-center mt-2">
              <a
                href={`/vault-passcode-reset/${encodeURIComponent(qr_code)}`}
                data-testid="vault-edit-gate-forgot-link"
                className="text-[11px] italic underline"
                style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}
              >
                Forgot your passcode?
              </a>
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  /* ── Render: editor (also handles read-only state when phase === 'locked') ── */
  const locked = phase === 'locked';
  const passcodeNote = meta?.has_password
    ? 'A passcode is set for this Memory Vault.'
    : 'No passcode set yet — add one below if you\u2019d like to keep this private.';

  return (
    <div className="min-h-screen" style={{ background: C.cream, color: C.text }} data-testid="vault-edit-page">
      {/* ── Top bar with save indicator ── */}
      <div
        className="sticky top-0 z-20 backdrop-blur-md"
        style={{ background: 'rgba(245, 241, 232, 0.92)', borderBottom: `1px solid ${C.border}` }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <a
            href="/memory-vault"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase transition-colors duration-300"
            style={{ color: C.gold }}
            data-testid="vault-edit-back-link"
            onClick={(e) => {
              // Prefer browser back when the user came from the order-success
              // selection screen (multi-vault flow). Falls back to the calm
              // /memory-vault landing — never the LTL /upload flow.
              try {
                if (window.history.length > 1 && document.referrer && /\/order-success/.test(document.referrer)) {
                  e.preventDefault();
                  window.history.back();
                }
              } catch { /* fall through to the href */ }
            }}
          >
            <ArrowLeft size={14} /> Back to Memory Vault
          </a>
          <div className="text-[11px] italic flex items-center gap-2" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }} data-testid="vault-edit-save-status">
            {locked ? (
              <><Lock size={12} style={{ color: C.gold }} /> Sealed · read-only</>
            ) : (
              <>
                {saveStatus === 'saving' && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
                {saveStatus === 'saved' && <><Check size={12} style={{ color: C.gold }} /> Saved</>}
                {saveStatus === 'error' && <><AlertCircle size={12} /> Couldn't save — try again</>}
                {saveStatus === 'idle' && <span style={{ opacity: 0.6 }}>Drafts save automatically</span>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="pt-16 pb-12 px-6">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-5" style={{ color: C.gold }} data-testid="vault-edit-hero-overline">
            Sent2ment · Memory Vault
          </p>
          <h1 className="text-4xl sm:text-5xl font-light leading-[1.1] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }} data-testid="vault-edit-hero-title">
            Quietly fill the spaces only you can fill.
          </h1>
          <p className="text-base sm:text-lg italic leading-relaxed mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.textSec }}>
            Add what feels right. Skip what doesn't. Come back as often as you need.
          </p>
          <p className="text-xs italic" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }} data-testid="vault-edit-passcode-note">
            {passcodeNote}
          </p>
        </motion.div>
      </section>

      {/* ── Sections ── */}
      <main className="max-w-3xl mx-auto px-6 pb-24">

        {/* Sealed banner — shown only when the vault has been finalised. */}
        {locked && (
          <motion.div
            {...fadeUp}
            className="mb-12 p-6 sm:p-7 text-center"
            style={{ background: C.goldSoft, border: `1px solid ${C.gold}55`, borderRadius: '4px' }}
            data-testid="vault-edit-sealed-banner"
          >
            <Check size={22} strokeWidth={1.5} style={{ color: C.gold }} className="mx-auto mb-3" />
            <p className="text-[10px] tracking-[0.4em] uppercase font-semibold mb-2" style={{ color: C.gold }}>Sealed With Care</p>
            <h2 className="text-xl sm:text-2xl font-light leading-snug mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
              This Memory Vault has been sealed.
            </h2>
            <p className="text-sm leading-relaxed mb-5" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
              Everything you shared is held exactly as you left it. Securely stored for 10 years &mdash; with the option to extend to 25 years.
            </p>
          </motion.div>
        )}

        {/* 0. Optional passcode — reuses existing password_hash (single shared passcode rule) */}
        {!locked && (
        <motion.section
          {...fadeUp}
          className="mb-16 p-7 sm:p-9"
          style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '4px' }}
          data-testid="vault-edit-section-passcode"
        >
          <SectionHeading
            Icon={Lock}
            overline="Optional · keep it private"
            title="A passcode for the Memory Vault"
            body="Add a passcode if you'd like only the right person to be able to open this. Leave it blank to let the vault open freely."
          />
          {meta?.has_password ? (
            <div data-testid="vault-edit-passcode-set">
              <p className="text-sm leading-relaxed mb-3" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
                A passcode is already set for this Memory Vault.
              </p>
              {meta?.password_hint && (
                <p className="text-xs italic" style={{ color: `${C.textSec}CC`, fontFamily: 'Georgia, serif' }}>
                  Hint on file: <span style={{ color: C.midnight }}>{meta.password_hint}</span>
                </p>
              )}
              <p className="text-[11px] italic mt-3" style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}>
                You can replace it below if needed.
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4">
                <input
                  type="password"
                  value={pwdCurrent}
                  onChange={(e) => setPwdCurrent(e.target.value)}
                  placeholder="Current passcode"
                  autoComplete="current-password"
                  data-testid="vault-edit-passcode-current-input"
                  className="w-full px-4 py-3 text-sm outline-none transition-colors"
                  style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
                />
                <input
                  type="password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="New passcode"
                  data-testid="vault-edit-passcode-input"
                  className="w-full px-4 py-3 text-sm outline-none transition-colors"
                  style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
                />
                <input
                  type="password"
                  value={pwdConfirm}
                  onChange={(e) => setPwdConfirm(e.target.value)}
                  placeholder="Confirm new passcode"
                  data-testid="vault-edit-passcode-confirm"
                  className="w-full px-4 py-3 text-sm outline-none transition-colors"
                  style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
                />
                <input
                  type="text"
                  value={pwdHint}
                  onChange={(e) => setPwdHint(e.target.value)}
                  placeholder="Optional hint (e.g. our anniversary)"
                  data-testid="vault-edit-passcode-hint"
                  maxLength={120}
                  className="w-full px-4 py-3 text-sm outline-none transition-colors"
                  style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4" data-testid="vault-edit-passcode-form">
              <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Passcode"
                data-testid="vault-edit-passcode-input"
                className="w-full px-4 py-3 text-sm outline-none transition-colors"
                style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
              />
              <input
                type="password"
                value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                placeholder="Confirm passcode"
                data-testid="vault-edit-passcode-confirm"
                className="w-full px-4 py-3 text-sm outline-none transition-colors"
                style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
              />
              <input
                type="text"
                value={pwdHint}
                onChange={(e) => setPwdHint(e.target.value)}
                placeholder="Optional hint (e.g. our anniversary)"
                data-testid="vault-edit-passcode-hint"
                maxLength={120}
                className="w-full px-4 py-3 text-sm outline-none transition-colors"
                style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
              />
            </div>
          )}
          <div className="mt-5 flex items-center gap-4 flex-wrap">
            <button
              type="button"
              onClick={handlePasscodeSave}
              disabled={pwdSaving || !pwd}
              data-testid="vault-edit-passcode-save-btn"
              className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: C.gold, color: '#3A342E' }}
            >
              {pwdSaving ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : (meta?.has_password ? 'Update passcode' : 'Save passcode')}
            </button>
            {meta?.has_password && (
              <button
                type="button"
                onClick={handlePasscodeRemove}
                disabled={pwdRemoving || !pwdCurrent}
                data-testid="vault-edit-passcode-remove-btn"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'transparent', color: C.textSec, border: `1px solid ${C.border}` }}
                title="Remove the passcode from this vault"
              >
                {pwdRemoving ? (<><Loader2 size={14} className="animate-spin" /> Removing…</>) : 'Remove passcode'}
              </button>
            )}
            {pwdMessage.text && (
              <p
                className="text-xs italic"
                style={{
                  color: pwdMessage.kind === 'error' ? '#B33A3A' : C.gold,
                  fontFamily: 'Georgia, serif',
                }}
                data-testid="vault-edit-passcode-message"
              >
                {pwdMessage.text}
              </p>
            )}
          </div>
          <p className="text-[11px] italic mt-4" style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}>
            One passcode for everything — same as your Letters That Last memory if you have one. No separate code.
          </p>
        </motion.section>
        )}

        {/* 1. Pocket keepsake — premium pocket card */}
        <motion.section
          {...fadeUp}
          className="mb-16"
          data-testid="vault-edit-section-pocket"
        >
          <PocketKeepsakeCardEdit
            value={textDraft.pocket_keepsake_message}
            onChangeText={handleTextChange}
            audioUrl={content.pocket_keepsake_audio_url}
            onUpload={handleAudioUpload}
            locked={locked}
            busy={!!audioBusy.pocket}
          />
        </motion.section>

        {/* 3. Three affirmations — tarot-style cards */}
        <motion.section {...fadeUp} className="mb-16 p-7 sm:p-9" style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '4px' }} data-testid="vault-edit-section-affirmations">
          <AffirmationsHeading />
          <AffirmationCardsEdit
            textDraft={textDraft}
            onChangeText={handleTextChange}
            content={content}
            onUpload={handleAudioUpload}
            locked={locked}
            audioBusy={audioBusy}
          />
        </motion.section>

        {/* 4. Interactive Journal — guided 30-page experience.
            Shown in BOTH editing and locked (sealed) modes so the sender
            can re-read everything they wrote even after sealing. The
            InteractiveJournalSection itself disables inputs / hides the
            audio upload control when `locked` is true. */}
        <InteractiveJournalSection
          values={{
            1: locked ? (content.journal_page_1_message || '') : (textDraft.journal_page_1_message || ''),
            7: locked ? (content.journal_page_7_message || '') : (textDraft.journal_page_7_message || ''),
            18: locked ? (content.journal_page_18_message || '') : (textDraft.journal_page_18_message || ''),
            30: locked ? (content.journal_page_30_message || '') : (textDraft.journal_page_30_message || ''),
          }}
          onChangePage={(pageNumber, next) => {
            if (locked) return;
            const fieldName = `journal_page_${pageNumber}_message`;
            handleTextChange(fieldName, next);
          }}
          audioUrl={content.vault_extra_audio_url}
          onAudioUpload={locked ? undefined : (file) => handleAudioUpload('extra', file)}
          audioBusy={!!audioBusy.extra}
          locked={locked}
        />

        {/* 5. Who should receive this? — optional recipient email */}
        {!locked && (
        <motion.section
          {...fadeUp}
          className="mb-16 p-7 sm:p-9"
          style={{ background: C.paper, border: `1px solid ${C.border}`, borderRadius: '4px' }}
          data-testid="vault-edit-section-recipient"
        >
          <SectionHeading
            Icon={Heart}
            overline="Optional · how it reaches them"
            title="Who should receive this?"
            body="Choose how the Memory Vault gets to them. Nothing is sent until you click Finalise &amp; Send."
          />
          <div className="space-y-3">
            <label
              className="flex items-start gap-3 px-5 py-4 cursor-pointer transition-colors duration-300 hover:bg-[#F5F1E8]/40"
              style={{
                background: sendMethod === 'self' ? '#F5F1E8' : '#FFFFFF',
                border: `1px solid ${sendMethod === 'self' ? C.gold : C.border}`,
                borderRadius: '4px',
              }}
              data-testid="vault-edit-send-self-option"
            >
              <input
                type="radio"
                name="vault-send-method"
                value="self"
                checked={sendMethod === 'self'}
                onChange={() => setSendMethod('self')}
                className="mt-1.5"
                style={{ accentColor: C.gold }}
                data-testid="vault-edit-send-self-radio"
              />
              <div className="flex-1">
                <p className="text-[14px] font-medium" style={{ color: C.midnight, fontFamily: 'Georgia, serif' }}>
                  I&rsquo;ll share this myself
                </p>
                <p className="text-xs italic mt-1" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
                  We won&rsquo;t email anyone. You&rsquo;ll get the link to share when you&rsquo;re ready.
                </p>
              </div>
            </label>

            <label
              className="flex items-start gap-3 px-5 py-4 cursor-pointer transition-colors duration-300 hover:bg-[#F5F1E8]/40"
              style={{
                background: sendMethod === 'direct' ? '#F5F1E8' : '#FFFFFF',
                border: `1px solid ${sendMethod === 'direct' ? C.gold : C.border}`,
                borderRadius: '4px',
              }}
              data-testid="vault-edit-send-direct-option"
            >
              <input
                type="radio"
                name="vault-send-method"
                value="direct"
                checked={sendMethod === 'direct'}
                onChange={() => setSendMethod('direct')}
                className="mt-1.5"
                style={{ accentColor: C.gold }}
                data-testid="vault-edit-send-direct-radio"
              />
              <div className="flex-1">
                <p className="text-[14px] font-medium" style={{ color: C.midnight, fontFamily: 'Georgia, serif' }}>
                  Send directly to them
                </p>
                <p className="text-xs italic mt-1" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
                  We&rsquo;ll email them when you finalise &mdash; with a link to open the Memory Vault.
                </p>
              </div>
            </label>
          </div>

          {sendMethod === 'direct' && (
            <div className="mt-6 space-y-4" data-testid="vault-edit-recipient-fields">
              <div>
                <label className="block text-[11px] tracking-[0.18em] uppercase font-medium mb-2" style={{ color: C.midnight }}>
                  Their email
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="name@example.com"
                  data-testid="vault-edit-recipient-email"
                  className="w-full px-4 py-3 text-sm outline-none transition-colors"
                  style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
                />
              </div>
              <div>
                <label className="block text-[11px] tracking-[0.18em] uppercase font-medium mb-2" style={{ color: C.midnight }}>
                  Personal message <span className="italic font-normal" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>(optional)</span>
                </label>
                <textarea
                  value={recipientMessage}
                  onChange={(e) => setRecipientMessage(e.target.value)}
                  placeholder="A short note they&rsquo;ll see at the top of the email&hellip;"
                  rows={4}
                  maxLength={1000}
                  data-testid="vault-edit-recipient-personal-message"
                  className="w-full px-4 py-3 text-[15px] outline-none transition-colors duration-300 resize-none"
                  style={{ background: '#FFFFFF', color: C.text, border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif', lineHeight: '1.7' }}
                />
              </div>
              <p className="text-[11px] italic" style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}>
                Nothing is sent until you click <span style={{ color: C.midnight, fontStyle: 'normal', fontWeight: 500 }}>Finalise &amp; Send</span>.
              </p>
            </div>
          )}
        </motion.section>
        )}

        {/* 6. Finalise */}
        {!locked && (
        <motion.section {...fadeUp} className="text-center pt-6" data-testid="vault-edit-finalise-block">
          <Lock size={20} strokeWidth={1.4} style={{ color: C.gold }} className="mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            Ready to seal the Memory Vault?
          </h2>
          <p className="text-sm leading-relaxed mb-3 max-w-xl mx-auto" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            Once finalised, the Memory Vault is kept exactly as you've left it — no further edits, no chance of accidental changes.
          </p>
          <p className="text-xs italic mb-8" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            They'll still be able to view it normally. There's no rush — you can always finalise later.
          </p>
          <button
            type="button"
            onClick={() => setFinaliseOpen(true)}
            data-testid="vault-edit-finalise-btn"
            className="inline-block font-semibold tracking-[0.22em] uppercase text-xs px-12 py-5 transition-all duration-500 hover:shadow-[0_8px_28px_rgba(198,169,107,0.35)]"
            style={{ background: C.gold, color: '#3A342E', borderRadius: '4px' }}
          >
            Finalise &amp; Send
          </button>
        </motion.section>
        )}
      </main>

      {/* ── Finalise confirmation modal ── */}
      <AnimatePresence>
        {finaliseOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6"
            style={{ background: 'rgba(11, 31, 58, 0.55)' }}
            onClick={() => !finalising && setFinaliseOpen(false)}
            data-testid="vault-edit-finalise-modal"
          >
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 16, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md p-8 sm:p-10 text-center"
              style={{ background: C.cream, border: `1px solid ${C.border}` }}
            >
              <Lock size={22} strokeWidth={1.4} style={{ color: C.gold }} className="mx-auto mb-5" />
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
                Seal this Memory Vault?
              </h3>
              <p className="text-sm leading-relaxed mb-7" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
                After finalising, no further changes can be made. They'll still be able to view it whenever they feel ready.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setFinaliseOpen(false)}
                  disabled={finalising}
                  data-testid="vault-edit-finalise-cancel"
                  className="flex-1 px-6 py-3 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300 disabled:opacity-50"
                  style={{ border: `1px solid ${C.border}`, color: C.midnight, background: 'transparent' }}
                >
                  Not yet
                </button>
                <button
                  type="button"
                  onClick={handleFinalise}
                  disabled={finalising}
                  data-testid="vault-edit-finalise-confirm"
                  className="flex-1 px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-all duration-300 disabled:opacity-60"
                  style={{ background: C.gold, color: '#3A342E' }}
                >
                  {finalising ? (<span className="inline-flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Sealing…</span>) : 'Yes, seal it'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VaultEditPage;
